<template>
  <nav id="navigation-mobile-header">
    <div id="navigation-mobile-controls">
      <router-link to="/" class="ps-icon" aria-label="Current projects">
        <img :src="psIconURL" alt="PreconSuite">
      </router-link>
      <button
        ref="menuButton"
        class="menu-toggle"
        type="button"
        aria-controls="navigation-mobile-menu"
        :aria-expanded="menuOpen"
        :aria-label="menuOpen ? 'Close navigation' : 'Open navigation'"
        @click="toggleMenu"
      >
        <o-icon :icon="menuOpen ? 'times' : 'bars'"/>
      </button>
    </div>

    <Teleport to="body">
      <Transition name="fade">
        <button
          v-if="menuOpen"
          class="menu-backdrop"
          type="button"
          aria-label="Close navigation"
          @click="closeMenu()"
        />
      </Transition>

      <Transition name="mobile-drawer">
        <div
          v-if="menuOpen"
          id="navigation-mobile-menu"
          ref="menuDrawer"
          aria-label="Main navigation"
          aria-modal="true"
          role="dialog"
          @keydown="handleKeydown"
        >
          <header class="menu-header">
            <router-link to="/" class="client" @click="closeMenu(false)">
              <img v-if="clientLogoURL" :src="clientLogoURL" :alt="clientName">
              <span v-else>{{ clientName }}</span>
            </router-link>
            <button
              class="close-button"
              type="button"
              aria-label="Close navigation"
              @click="closeMenu()"
            >
              <o-icon icon="times"/>
            </button>
          </header>

          <div class="menu-content">
            <section aria-labelledby="mobile-navigation-heading">
              <h2 id="mobile-navigation-heading">Navigation</h2>
              <NavMenu :menu="primaryMenu" @click="closeMenu(false)"/>
              <BidPackagesList @click="closeMenu(false)"/>
            </section>

            <section aria-labelledby="mobile-tools-heading">
              <h2 id="mobile-tools-heading">Tools</h2>
              <NavMenu :menu="toolsMenu" @click="closeMenu(false)"/>
            </section>

            <section aria-labelledby="mobile-account-heading">
              <h2 id="mobile-account-heading">Account &amp; support</h2>
              <NavMenu :menu="accountMenu" @click="closeMenu(false)"/>
            </section>
          </div>
        </div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {useRoute, useRouter} from 'vue-router';

import BidPackagesList from '@/pages/Projects/BidPackagesList.vue';
import NavMenu from '@/components/NavMenu.vue';
import globalMenu from '@/menus/global';
import primaryMenuDefinition from '@/menus/primary';
import userMenu from '@/menus/user';
import {findRouteByName, type Menu} from '@/internal';
import {useCompanyStore} from '@/stores/CompanyStore';
import {useClientStore} from '@/stores/ClientStore';
import {useSystemStore} from '@/stores/SystemStore';

const route = useRoute();
const router = useRouter();
const systemStore = useSystemStore();
const {company} = storeToRefs(useCompanyStore());
const {client, psIconURL} = storeToRefs(useClientStore());
const emit = defineEmits<{
  'update:open': [open: boolean];
}>();

const menuOpen = ref(false);
const menuButton = ref<HTMLButtonElement | null>(null);
const menuDrawer = ref<HTMLElement | null>(null);

const clientLogoURL = computed(() => client.value?.logo_url || company.value?.client?.logo_url || null);
const clientName = computed(() => client.value?.name || company.value?.client?.name || 'SubsPortal');

const primaryMenu = computed<Menu>(() => primaryMenuDefinition({
  handleTakeOffSoftwareClick: () => {
    closeMenu(false);
    window.open(import.meta.env.VITE_TAKE_OFF_SOFTWARE_URL, '_blank');
  },
}));

const toolsMenu = computed<Menu>(() => {
  const menu = globalMenu({
    handleFavoritesClick: () => openPanel('favorites'),
    handleSearchClick: () => openPanel('search'),
    handleAddClick: () => openPanel('add'),
    hideTitles: false,
  });

  menu.menuItems = menu.menuItems.filter(item => !item.usePsIcon);
  return menu;
});

const accountMenu = computed<Menu>(() => userMenu({
  handleAccountClick: () => openPanel('account'),
  handleHelpClick: () => openPanel('help'),
  handleSystemPreferencesClick: async () => {
    closeMenu(false);
    await router.push(findRouteByName('settings/system-preferences'));
  },
  handleLogOutClick: async () => {
    closeMenu(false);
    await router.push(findRouteByName('logout'));
  },
  handleSuperClick: () => openPanel('super'),
  hideTitles: false,
  numUnreadNotifications: null,
  user: {},
}));

function toggleMenu() {
  menuOpen.value ? closeMenu() : openMenu();
}

async function openMenu() {
  if (systemStore.navPanelOpen) {
    systemStore.toggleNavPanel('');
  }

  menuOpen.value = true;
  emit('update:open', true);
  await nextTick();
  menuDrawer.value?.querySelector<HTMLElement>('button, a')?.focus();
}

function closeMenu(restoreFocus = true) {
  menuOpen.value = false;
  emit('update:open', false);
  if (restoreFocus) nextTick(() => menuButton.value?.focus());
}

function openPanel(panel: string) {
  closeMenu(false);
  systemStore.toggleNavPanel(panel);
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeMenu();
    return;
  }

  if (event.key !== 'Tab' || !menuDrawer.value) return;

  const focusable = Array.from(menuDrawer.value.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  ));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}

watch(() => route.fullPath, () => closeMenu(false));
onBeforeUnmount(() => emit('update:open', false));
</script>

<style scoped lang="scss">
#navigation-mobile-header {
  position: relative;
  z-index: 3500;

  #navigation-mobile-controls {
    align-items: center;
    background: var(--nav-background);
    display: flex;
    height: 56px;
    justify-content: space-between;
    left: 0;
    padding: 0 8px;
    position: fixed;
    right: 0;
    top: 0;

    .ps-icon {
      align-items: center;
      display: flex;
      height: 44px;
      padding: 6px 10px;

      img {
        height: 30px;
      }
    }

    .menu-toggle {
      align-items: center;
      background: transparent;
      border: 0;
      border-radius: 6px;
      color: var(--nav-text);
      cursor: pointer;
      display: flex;
      height: 44px;
      justify-content: center;
      width: 44px;

      &:focus-visible {
        outline: 2px solid $white;
        outline-offset: -3px;
      }
    }
  }

}

.menu-backdrop {
    background: rgba($black, .45);
    border: 0;
    bottom: 0;
    cursor: default;
    left: 0;
    padding: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 3501;
  }

#navigation-mobile-menu {
    background: var(--nav-secondary-background);
    color: var(--nav-secondary-text);
    bottom: 0;
    box-shadow: 8px 0 30px rgba($black, .2);
    display: flex;
    flex-direction: column;
    left: 0;
    position: fixed;
    top: 0;
    width: min(88vw, 360px);
    z-index: 3502;

    label{
      color: var(--nav-secondary-text);
    }

    .menu-header {
      align-items: center;
      border-bottom: 1px solid var(--nav-secondary-border-color);
      display: flex;
      flex: 0 0 auto;
      justify-content: space-between;
      min-height: 64px;
      padding: 8px 12px 8px 20px;

      .client {
        align-items: center;
        color: var(--nav-secondary-text);
        display: flex;
        font-size: 1.05rem;
        font-weight: 600;
        max-width: calc(100% - 52px);
        min-height: 44px;
        min-width: 0;

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        img {
          max-height: 36px;
          max-width: 190px;
        }
      }

      .close-button {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 50%;
        color: var(--nav-secondary-text);
        cursor: pointer;
        display: flex;
        height: 44px;
        justify-content: center;
        width: 44px;

        &:hover,
        &:focus-visible {
          background: var(--nav-secondary-background-hover);
        }

        &:focus-visible {
          outline: 2px solid $primary;
        }
      }
    }

    .menu-content {
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 12px 16px 24px;

      section + section {
        border-top: 1px solid var(--nav-secondary-border-color);
        margin-top: 8px;
        padding-top: 8px;
      }

      h2 {
        color: var(--nav-secondary-text);
        font-size: .72rem;
        font-weight: 700;
        letter-spacing: .08em;
        margin: 8px 12px 4px;
        text-transform: uppercase;
      }

      :deep(.nav-menu) {
        background: transparent;
        margin: 0;
        padding: 0;

        .menu-item {
          border-radius: 8px;
          margin: 2px 0;
          min-height: 44px;

          .menu-item {
            padding-left: 20px;
          }

          &.item-active,
          &.router-link-exact-active {
            background: transparent;
            color: var(--nav-secondary-text);
          }
        }

        .icon-only {
          border-radius: 8px;
          height: 44px;
        }
      }

      :deep(.bid-packages) {
        border-top: 1px solid var(--nav-secondary-border-color);
        margin: 8px 0 0;
        padding-top: 12px;
      }
    }
  }

.mobile-drawer-enter-active,
.mobile-drawer-leave-active,
.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s ease, transform .2s ease;
  }

.mobile-drawer-enter-from,
.mobile-drawer-leave-to {
    transform: translateX(-100%);
  }

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
  }

@media (prefers-reduced-motion: reduce) {
  .mobile-drawer-enter-active,
  .mobile-drawer-leave-active,
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
